import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { MembershipProvider } from "./context/membershipContext";
import * as Sentry from "@sentry/react";
import ScrollToTop from "./components/ui/ScrollToTop";

// URLs whose fetch failures are not actionable — legacy app.js shell requests
// that are intercepted by the service worker on warm loads. Failures on first
// load are cosmetic only (nav/footer) and do not disrupt the membership flow.
const SUPPRESSED_URLS = [
  "www.aanp.org/menu.json",
  "www.aanp.org/footer.json",
  "www.aanp.org/social.json",
  "www.aanp.org/utility.json",
  "www.aanp.org/messages.json",
];

Sentry.init({
  dsn: "https://faa70bfc57114de98e656cc0534ae2a0@o148073.ingest.sentry.io/5868776",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event, hint) {
    const error = hint?.originalException;

    // Browser aborts in-flight fetches when navigation occurs — not actionable
    if (error?.name === "AbortError") {
      return null;
    }

    // Sentry v8 passes breadcrumbs as a direct array on the event object.
    // Older types described it as { values: [] } — guard for both shapes.
    const breadcrumbs = Array.isArray(event.breadcrumbs)
      ? event.breadcrumbs
      : Array.isArray(event.breadcrumbs?.values)
        ? event.breadcrumbs.values
        : [];

    // Check whether any breadcrumb references a shell data URL with a
    // non-200 status (network failure, 500, etc.). Used by both rules below.
    const hasFailedShellBreadcrumb = breadcrumbs.some((b) => {
      const isShellUrl = SUPPRESSED_URLS.some((u) => b.data?.url?.includes(u));
      const isFailed =
        b.data?.status_code === undefined || // fetch error (no status)
        b.data?.status_code === 0 ||         // network error
        b.data?.status_code >= 400;          // 4xx / 5xx from server
      return isShellUrl && isFailed;
    });

    // Any fetch/network error on a shell URL — cosmetic only
    if (hasFailedShellBreadcrumb && error?.name === "TypeError" &&
      (error?.message === "Load failed" ||
        error?.message === "Failed to fetch")) {
      return null;
    }

    // Any downstream TypeError that co-occurs with a failed shell URL request.
    // app.js crashes with various minified variable names (n.data, i.children,
    // l.some, etc.) depending on the build — match by context, not by name.
    if (hasFailedShellBreadcrumb && error?.name === "TypeError") {
      return null;
    }

    return event;
  },
});

ReactDOM.render(
  <React.StrictMode>
    <BrowserRouter>
      <MembershipProvider>
        <ScrollToTop />
        <App />
      </MembershipProvider>
    </BrowserRouter>
  </React.StrictMode>,
  document.getElementById("root")
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
